import React, { createContext, useContext, useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Badges werden deutlich seltener aktualisiert als ein offener Chat.
const POLL_INTERVAL_MS = 30000;

const UnreadMessagesContext = createContext({ total: 0, byRequest: {}, refresh: () => {} });

/**
 * Ein einziger Poller fuer alle Ungelesen-Badges (Navbar, Tabs, Karten).
 * Serverseitig ist das eine indizierte Abfrage ueber messages.recipient_id.
 */
export const UnreadMessagesProvider = ({ user, children }) => {
  const [state, setState] = useState({ total: 0, byRequest: {} });
  const inFlight = useRef(false);
  const alive = useRef(true);

  const refresh = useCallback(async () => {
    if (!user || inFlight.current) return;
    if (document.visibilityState !== 'visible') return;
    inFlight.current = true;
    try {
      const token = localStorage.getItem('token');
      if (!token) return;
      const res = await axios.get(`${API}/messages/unread-summary`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!alive.current) return;
      setState({ total: res.data.total || 0, byRequest: res.data.by_request || {} });
    } catch (e) {
      // Badges sind unkritisch - stillschweigend ueberspringen
    } finally {
      inFlight.current = false;
    }
  }, [user]);

  useEffect(() => {
    alive.current = true;
    if (!user) {
      setState({ total: 0, byRequest: {} });
      return () => { alive.current = false; };
    }

    refresh();
    const timer = setInterval(refresh, POLL_INTERVAL_MS);
    const onVisible = () => {
      if (document.visibilityState === 'visible') refresh();
    };
    document.addEventListener('visibilitychange', onVisible);

    return () => {
      alive.current = false;
      clearInterval(timer);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [user, refresh]);

  return (
    <UnreadMessagesContext.Provider value={{ ...state, refresh }}>
      {children}
    </UnreadMessagesContext.Provider>
  );
};

export const useUnreadMessages = () => useContext(UnreadMessagesContext);

export default useUnreadMessages;
